import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { GamePhase } from '@susdle/shared';
import type { LobbySession, Snapshot } from './LobbySession.js';
import type { LobbyView } from '../lobby/types.js';
import type { MeetingView } from '../ghost-meeting/types.js';
import type { RoleRevealView, GuessRevealView } from '../discussion/types.js';

/**
 * Length of the synthesized meeting wind-down window (the `settling` beat).
 * The server transitions GhostMeeting → ClueGiving without warning the client,
 * so the router holds the meeting on screen for this long after observing the
 * boundary, with `meetingSettling=true` — `MysteryFlipGrid` halts new shuffle
 * swaps and lets any in-flight FLIP transition ease to a rest instead of being
 * hard-cut. ~600ms: long enough for the swap tail to settle, short enough that
 * the eye doesn't dwell on a frozen grid. MockApp's "Continue to Clue-Giving"
 * affordance uses the same constant so mock and production timing match.
 */
export const MEETING_SETTLE_MS = 600;

export interface PhaseRouter {
  phase: GamePhase;
  bootPhase: Snapshot['bootPhase'];
  error: Snapshot['error'];
  connection: Snapshot['connection'];
  clockOffset: number;
  /** True only during a clean Lobby → GhostMeeting transition (play the page-turn). */
  transitioning: boolean;
  /** Lobby snapshot frozen at the Lobby → GhostMeeting boundary; feeds the cinematic. */
  frozenLobbyView: LobbyView | null;
  /** True for the first meeting render after a cold arrival (boot-into-meeting OR
   *  reconnect-mid-meeting); suppresses entry beats. Cleared after one paint. */
  justArrived: boolean;
  /** Clears `transitioning` once the cinematic completes (wire to GhostMeeting's onTransitionComplete). */
  completeTransition: () => void;
  /** True only during a clean transition INTO Clue-Giving (play the entry beat).
   *  Independent of the meeting page-turn `transitioning` (handoff §10-Q1). */
  cluegivingTransitioning: boolean;
  /** Clears `cluegivingTransitioning` (wire to CluegivingPhase's onTransitionComplete). */
  completeCluegivingTransition: () => void;
  /** True for `MEETING_SETTLE_MS` after a clean GhostMeeting → ClueGiving
   *  transition: the App keeps the meeting mounted (off `frozenMeetingView`)
   *  with `settling=true` so the V/F mystery grid winds down instead of
   *  hard-cutting. Self-clears on a timer; false on cold arrivals and on
   *  every non-meeting ClueGiving entry (round 2+). */
  meetingSettling: boolean;
  /** Meeting snapshot frozen at the GhostMeeting → ClueGiving boundary; feeds
   *  the settle-window render (the live `meetingView` nulls out the moment the
   *  phase flips). Mirrors frozenLobbyView's pattern. */
  frozenMeetingView: MeetingView | null;
  /** True only during a clean live arrival into GameEnd (play the close/burn cinematic).
   *  Stays false on cold arrival; set on any clean prevPhase → GameEnd transit
   *  including AFK arrivals from Discussion/ClueGiving. The stalemate Lights-Out
   *  surface is gated by view.side === 'none' in App.tsx, not here. */
  gameEndTransitioning: boolean;
  /** Frozen snapshot of the prior phase's view when the predecessor was a cinematic
   *  phase (RoleReveal | GuessReveal). Null for AFK arrivals, cold arrivals, and
   *  stalemate. Mirrors frozenLobbyView's pattern for the Lobby → GhostMeeting transit. */
  frozenPriorPhase:
    | { phase: 'role-reveal'; view: RoleRevealView }
    | { phase: 'guess-reveal'; view: GuessRevealView }
    | null;
  /** Clears `gameEndTransitioning` once the cinematic settles (wire to GameEnd's onTransitionComplete). */
  completeGameEndTransition: () => void;
}

export function usePhaseRouter(session: LobbySession): PhaseRouter {
  const snapshot = useSyncExternalStore(
    (cb) => session.subscribe(cb),
    () => session.getSnapshot(),
  );
  const { phase, bootPhase, error, connection, clockOffset, view } = snapshot;

  const [transitioning, setTransitioning] = useState(false);
  const [frozenLobbyView, setFrozenLobbyView] = useState<LobbyView | null>(null);
  const [cluegivingTransitioning, setCluegivingTransitioning] = useState(false);
  const [meetingSettling, setMeetingSettling] = useState(false);
  const [frozenMeetingView, setFrozenMeetingView] = useState<MeetingView | null>(null);
  const [gameEndTransitioning, setGameEndTransitioning] = useState(false);
  const [frozenPriorPhase, setFrozenPriorPhase] = useState<
    | { phase: 'role-reveal'; view: RoleRevealView }
    | { phase: 'guess-reveal'; view: GuessRevealView }
    | null
  >(null);

  /* Last non-null lobby view, so the cinematic can carry the lobby that was on
     screen the instant the meeting phase arrived. */
  const lastLobbyViewRef = useRef<LobbyView | null>(null);
  if (view !== null) lastLobbyViewRef.current = view;

  /* Last non-null meeting view, so the settle window can keep rendering the
     meeting that was on screen the instant the clue-giving phase arrived
     (the snapshot's meetingView nulls out on the phase flip). */
  const lastMeetingViewRef = useRef<MeetingView | null>(null);
  if (snapshot.meetingView !== null) lastMeetingViewRef.current = snapshot.meetingView;

  /* Last non-null reveal views, so GameEnd's cinematic can carry the
     RoleReveal/GuessReveal that was on screen the instant GameEnd arrived.
     Mirrors lastLobbyViewRef. */
  const lastRoleRevealViewRef = useRef<RoleRevealView | null>(null);
  const lastGuessRevealViewRef = useRef<GuessRevealView | null>(null);
  if (snapshot.roleRevealView !== null) lastRoleRevealViewRef.current = snapshot.roleRevealView;
  if (snapshot.guessRevealView !== null) lastGuessRevealViewRef.current = snapshot.guessRevealView;

  /* Previous-render snapshot. READ at render-time (below) to derive
     `justArrived` synchronously; WRITTEN at the end of the post-commit effect
     so the next render sees the just-rendered values as "previous". The reads
     happen BEFORE the effect's writes within a single render cycle, so render
     always observes the prior render's values. */
  const prevPhaseRef = useRef<GamePhase | null>(null);
  const prevBootRef = useRef<Snapshot['bootPhase'] | null>(null);

  /* Derive `justArrived` in render so downstream components (ClueSlip,
     AvatarFrame, VoterCluster) observe `true` on the FIRST render after a
     cold arrival. They capture the suppression into refs at first mount, so
     a `setJustArrived(true)` from a post-commit effect arrives too late —
     the refs have already latched `false`. A cold arrival is either the first
     phase we ever observe (initial render with prevPhase === null) OR a
     reconnect resolving mid-phase (prevBoot === 'reconnecting' && bootPhase
     === 'ready'). Only mid-game phases need suppression; Lobby never animates
     in this way. On the render AFTER the effect updates the refs, the same
     derivation naturally yields `false`. */
  const prevPhase = prevPhaseRef.current;
  const prevBoot = prevBootRef.current;
  const coldArrival =
    prevPhase === null ||
    (prevBoot === 'reconnecting' && bootPhase === 'ready');
  const justArrived = coldArrival && phase !== GamePhase.Lobby;

  useEffect(() => {
    /* Snapshot the prior values BEFORE updating the refs. The render above
       already read the same values (refs are mutated below, not in render). */
    const prevPhaseLocal = prevPhaseRef.current;
    const prevBootLocal = prevBootRef.current;
    /* Latch `phase` only from 'ready' renders. Before the first 'ready' the
       snapshot's phase is the constructor default (Lobby) — the session has
       not yet seen server state. (LobbySession defers its first publication
       and the 'ready' flip to the first ROOM_STATE, but this hook is mounted
       during boot and renders the default snapshot.) Latching that phantom
       Lobby would consume the `prevPhase === null` cold signal before the
       join resolves, making a reload into a mid-game phase read as a live
       Lobby → X transition — the entry cinematic replays unearned (the
       reconnect.e2e.spec regression). The same gate freezes prevPhase across
       'reconnecting', matching the existing prevBoot-based classifier.
       `prevBoot` itself latches unconditionally: the classifier below needs
       to observe 'reconnecting'. */
    if (bootPhase === 'ready') prevPhaseRef.current = phase;
    prevBootRef.current = bootPhase;

    /* Reconnect classifier; coldArrival is recomputed locally (it must match
       what render computed). */
    const reconnecting = prevBootLocal === 'reconnecting' || bootPhase === 'reconnecting';
    const coldArrivalLocal =
      prevPhaseLocal === null ||
      (prevBootLocal === 'reconnecting' && bootPhase === 'ready');

    if (phase === GamePhase.GhostMeeting) {
      const cleanFromLobby = prevPhaseLocal === GamePhase.Lobby && !reconnecting;
      if (cleanFromLobby) {
        if (lastLobbyViewRef.current !== null) setFrozenLobbyView(lastLobbyViewRef.current);
        setTransitioning(true);
      } else if (coldArrivalLocal) {
        setTransitioning(false);
      }
      /* The remaining case — phase===GhostMeeting while bootPhase==='reconnecting'
         (phase observed mid-flight before the reconnect resolves) — intentionally
         no-ops. It's unreachable for a *fresh* mount: a reconnect-mid-meeting keeps
         this component mounted (suppression already captured at the first mount),
         and reconnect-resolve later hits the `coldArrival` branch via
         prevBoot==='reconnecting' && bootPhase==='ready'. So no entry state is
         stranded. */
      return;
    }

    if (phase === GamePhase.ClueGiving) {
      /* The entry cinematic ("the playing order has been decided" + V/F flip
         cascade) is meaningful only on the FIRST clue-giving — fresh from the
         ghost meeting where the order was just voted on. On subsequent rounds
         (Discussion → ClueGiving), the order is already known and re-playing
         the interstitial reads as a lie. Gate the beat on prevPhase ===
         GhostMeeting so only round 1 plays the cinematic; round 2+ enters
         cleanly with no transitioning flag and no cold-arrival suppression.
         Excluding ClueGiving → ClueGiving keeps a no-op same-phase state
         patch from re-triggering anything. */
      const cleanEntry = prevPhaseLocal !== null && prevPhaseLocal !== GamePhase.ClueGiving && !reconnecting;
      const fromMeeting = prevPhaseLocal === GamePhase.GhostMeeting;
      if (cleanEntry && fromMeeting) {
        setCluegivingTransitioning(true);
        /* Synthesize the meeting wind-down window: hold the meeting on screen
           for MEETING_SETTLE_MS with settling=true (the V/F mystery grid eases
           to rest instead of hard-cutting). The window self-clears via the
           timer effect below; CluegivingPhase then mounts with the entry-beat
           flag (set above) still armed. Skipped when no meeting view was ever
           captured (defensive — a clean fromMeeting entry implies one). */
        if (lastMeetingViewRef.current !== null) {
          setFrozenMeetingView(lastMeetingViewRef.current);
          setMeetingSettling(true);
        }
      } else if (cleanEntry) {
        setCluegivingTransitioning(false);
        setMeetingSettling(false);
      } else if (coldArrivalLocal) {
        setCluegivingTransitioning(false);
        setMeetingSettling(false);
      }
      /* As with GhostMeeting above: the remaining case — phase===ClueGiving while
         a reconnect is still in flight (neither cleanEntry nor coldArrival fires) —
         intentionally no-ops. The component stays mounted across the drop, and the
         reconnect-resolve re-enters this effect via prevBoot==='reconnecting' &&
         bootPhase==='ready' → coldArrival. So no entry state is stranded. */
      return;
    }

    /* Discussion / RoleReveal / GhostGuessing / GuessReveal: no per-phase
       persistent flags besides justArrived (now derived in render). */

    if (phase === GamePhase.GameEnd) {
      // Clean live arrival from ANY in-game phase. This includes:
      //   - RoleReveal  → GameEnd  (village elim, ghost parity via visible reveal)
      //   - GuessReveal → GameEnd  (ghosts by guess)
      //   - Discussion  → GameEnd  (stalemate via consecutive-rounds cap, OR
      //                             ghost parity via Discussion AFK whose 0ms
      //                             RoleReveal the client did not observe due
      //                             to Colyseus state-batching — verified by
      //                             Task 0's probe)
      //   - ClueGiving  → GameEnd  (ghost parity via ClueGiving AFK — same
      //                             0ms-RoleReveal-not-observed shape)
      // The cinematic gate is `transitioning`; the App distinguishes the
      // stalemate Lights-Out (view.side === 'none', no cinematic) from book
      // outcomes via the view, not the router.
      const cleanLiveArrival =
        prevPhaseLocal !== null && prevPhaseLocal !== GamePhase.GameEnd && !reconnecting;
      if (cleanLiveArrival) {
        // frozenPriorPhase captures ONLY when the predecessor is a cinematic
        // predecessor (RoleReveal or GuessReveal). For AFK arrivals
        // (Discussion / ClueGiving → GameEnd), frozenPriorPhase stays null —
        // the cinematic renders with an empty underlay.
        const frozen =
          prevPhaseLocal === GamePhase.RoleReveal && lastRoleRevealViewRef.current !== null
            ? ({ phase: 'role-reveal' as const, view: lastRoleRevealViewRef.current } as const)
            : prevPhaseLocal === GamePhase.GuessReveal && lastGuessRevealViewRef.current !== null
              ? ({ phase: 'guess-reveal' as const, view: lastGuessRevealViewRef.current } as const)
              : null;
        setFrozenPriorPhase(frozen);
        setGameEndTransitioning(true);
      } else if (coldArrivalLocal) {
        setFrozenPriorPhase(null);
        setGameEndTransitioning(false);
      }
      /* Remaining case (phase===GameEnd while reconnecting mid-flight)
         intentionally no-ops; component stays mounted, reconnect-resolve
         re-enters via prevBoot==='reconnecting' && bootPhase==='ready'
         → coldArrival. Same precedent as the GhostMeeting/ClueGiving branches. */
      return;
    }
  }, [phase, bootPhase]);

  /* Settle-window timer: meetingSettling self-clears after MEETING_SETTLE_MS.
     Cleanup covers re-arming and unmount; a phase change mid-window (only
     reachable through pathological timing — clue-giving lasts far longer than
     the window) self-heals when the timer fires because the App's settle
     render is additionally gated on phase === ClueGiving. */
  useEffect(() => {
    if (!meetingSettling) return undefined;
    const handle = window.setTimeout(() => setMeetingSettling(false), MEETING_SETTLE_MS);
    return () => window.clearTimeout(handle);
  }, [meetingSettling]);

  const completeTransition = useCallback(() => setTransitioning(false), []);
  const completeCluegivingTransition = useCallback(() => setCluegivingTransitioning(false), []);
  const completeGameEndTransition = useCallback(() => setGameEndTransitioning(false), []);

  return {
    phase, bootPhase, error, connection, clockOffset,
    transitioning, frozenLobbyView, justArrived, completeTransition,
    cluegivingTransitioning, completeCluegivingTransition,
    meetingSettling, frozenMeetingView,
    gameEndTransitioning, frozenPriorPhase, completeGameEndTransition,
  };
}
